import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { createContactSubmission } from "@/integrations/backend/contact-submissions";

export default function ContactPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await createContactSubmission({ name, email, phone, subject, message });
      setSent(true);
      setName(""); setEmail(""); setPhone(""); setSubject(""); setMessage("");
    } catch (err: any) {
      console.error("[contact] submission failed", err);
      // Error will be handled by the unhandled promise rejection handler
    } finally {
      setSending(false);
    }
  };

  const hero =
    "https://xnjrgbjrqhbqsfocowbt.supabase.co/storage/v1/object/public/marketing/karsten-winegeart-5sGYotxAuUA-unsplash.jpg";

  return (
    <>
      <MarketingHeader />
      <main className="min-h-screen gradient-dark pt-24">
        {/* Hero */}
        <section className="relative min-h-[45vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src={hero} alt="Contact hero" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-dark-bg/95 via-dark-bg/80 to-dark-bg" />
          </div>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/25 to-transparent" style={{ left: 'calc(50% - 220px)' }} />
            <div className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent" style={{ left: 'calc(50% + 220px)' }} />
          </div>
          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-5xl md:text-6xl font-black text-white leading-tight">Contact Us</h1>
            <p className="text-gray-light text-xl md:text-2xl mt-3">We’d love to hear from you. Reach out and we’ll get back fast.</p>
          </div>
        </section>

        {/* Content */}
        <section className="py-16">
          <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-12 max-w-6xl">
            <div className="space-y-6">
              <div className="bg-dark-card/80 backdrop-blur-sm border border-primary/15 rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-white mb-4">Company</h2>
                <p className="text-gray-light">Frequent Health, Inc.</p>
                <p className="text-gray-light">
                  <a href="mailto:info@healthfq.com" className="hover:text-primary transition-colors">
                    info@healthfq.com
                  </a>
                </p>
              </div>
              <div className="bg-dark-card/80 backdrop-blur-sm border border-primary/15 rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-white mb-4">What to expect</h2>
                <p className="text-gray-light">Our team replies within 1 business day. For account issues, use your signup email so we can verify you quickly.</p>
              </div>
            </div>

            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.98, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="relative overflow-hidden bg-dark-card/90 backdrop-blur-sm border border-primary/20 rounded-2xl p-10 text-center"
              >
                <div className="pointer-events-none absolute inset-0">
                  <div className="absolute -top-14 -left-10 w-64 h-64 rounded-full bg-primary/10 blur-3xl animate-pulse" />
                  <div className="absolute -bottom-16 -right-12 w-72 h-72 rounded-full bg-primary/10 blur-3xl animate-pulse" style={{ animationDelay: '0.8s' }} />
                </div>

                <div className="mx-auto w-24 h-24 rounded-full bg-primary/15 border border-primary/40 flex items-center justify-center shadow-neon-glow mb-6 relative">
                  <CheckCircle2 className="w-14 h-14 text-primary" />
                  <div className="absolute inset-0 rounded-full ring-1 ring-primary/30 animate-pulse" />
                </div>

                <h2 className="text-3xl md:text-4xl font-black text-white">Thanks for contacting us!</h2>
                <p className="text-gray-light mt-3 max-w-xl mx-auto">
                  We received your message and will reply within 1 business day. Keep an eye on your inbox.
                </p>

                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link to="/">
                    <Button variant="outline" className="border-primary/40 text-white hover:bg-primary/10">
                      Back to Home
                    </Button>
                  </Link>
                  <Button onClick={() => setSent(false)} className="font-bold uppercase tracking-wider shadow-neon-glow">
                    Send Another Message
                  </Button>
                </div>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="bg-dark-card/80 backdrop-blur-sm border border-primary/15 rounded-2xl p-8 space-y-4">
                <h2 className="text-3xl font-bold text-white">Send a message</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-gray-light">Name</Label>
                    <Input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" required />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-light">Email</Label>
                    <Input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@example.com" className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" required />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-light">Phone</Label>
                    <Input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="Optional" className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-gray-light">Subject</Label>
                    <Input value={subject} onChange={e=>setSubject(e.target.value)} placeholder="How can we help?" className="bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-light">Message</Label>
                  <Textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="Tell us more..." className="min-h-[160px] bg-dark-slate border-primary/20 text-white placeholder:text-gray-mid" required />
                </div>
                <Button type="submit" disabled={sending} className="font-bold uppercase tracking-wider shadow-neon-glow hover:scale-105 transition-all">
                  {sending ? 'Sending...' : sent ? 'Sent ✓' : 'Send Message'}
                </Button>
              </form>
            )}
          </div>
        </section>
      </main>
      <MarketingFooter />
    </>
  );
}




